import { useTranslation } from "react-i18next";
import formatPrice from "@/core/helpers/formatPrice";
import { BoxIcon, BucketIcon, Info2Icon } from "@/shared/icons";
import { Button, Tooltip } from "@/shared/ui";
import { CartItem } from "@/domains/business/slice";
import { getBusinessCardImage } from "@/domains/business/helpers/getBusinessImage";
import GoodsAndWarehouseTooltip from "@/domains/business/components/goodsAndWarehouse/GoodsAndWarehouseTooltip";

type Props = {
  item: CartItem;
  handleCountChange: (id: number, count: number) => void;
  handleRemove: (id: number) => void;
};

export default function GoodsAndWarehouseCartCard({
  item,
  handleCountChange,
  handleRemove,
}: Props) {
  const { t } = useTranslation("business");

  const tooltipContent = <GoodsAndWarehouseTooltip title={item.name} description={item.name} />;

  return (
    <div className="flex w-full h-[9.375rem] p-5 bg-dark-800 rounded-[0.75rem]">
      <div className="relative flex flex-col w-40 h-full mr-4 bg-[url(/svg/shop/dots.svg)] bg-center bg-no-repeat">
        <div
          className="absolute z-1 top-1/2 left-1/2 size-20 -translate-x-1/2 -translate-y-1/2 bg-contain bg-no-repeat bg-center"
          style={{ backgroundImage: `url(${getBusinessCardImage(item.templateId, "materials")})` }}
        />
        <Tooltip content={tooltipContent} tooltipClassName="-top-7 left-5 z-3">
          <Info2Icon className="absolute -left-2 -top-2 z-1 size-4.5 opacity-30 cursor-pointer hover:opacity-100" />
        </Tooltip>
      </div>
      <div className="flex flex-col justify-center w-[24.125rem] py-3">
        <p className="mb-2.5 text-16 font-medium">{item.name}</p>
        <p className="text-16 font-bold text-accent-solid">{formatPrice(item.price)}₽</p>
        <div className="flex items-center mt-auto">
          <BoxIcon className="size-3.5 text-white/30" />
          <p className="ml-1 text-14 font-medium text-white/60 leading-none">
            {t("business.goodsAndWarehouse.inWarehouse")}:
          </p>
          <p className="ml-1.5 text-12 font-bold leading-none">
            {item.warehouseAmount} {t("business.goodsAndWarehouse.cnt")}
          </p>
        </div>
      </div>
      <div className="flex flex-col gap-1.5 w-[11.75rem] ml-auto">
        <div className="flex gap-1.5">
          <div className="flex w-full h-10.5 p-1 bg-dark-900 rounded-xl">
            <Button
              className="relative size-8.5 rounded-xl"
              icon
              onClick={() => handleCountChange(item.id, item.count - 1)}
              variant="soft"
              color="gray"
            >
              <span className="absolute size-full top-0 left-0 bg-gradient-accent rounded-xl opacity-0 transition-all ease-in-out duration-200 hover:opacity-100" />
              <span className="relative z-1 w-2.5 h-0.5 bg-white pointer-events-none" />
            </Button>
            <p className="flex justify-center items-center grow text-14 font-bold">{item.count}</p>
            <Button
              className="group relative size-8.5 rounded-xl"
              icon
              onClick={() => handleCountChange(item.id, item.count + 1)}
              variant="soft"
              color="gray"
            >
              <span className="absolute size-full top-0 left-0 bg-gradient-accent rounded-xl opacity-0 transition-all ease-in-out duration-200 hover:opacity-100" />
              <span className="relative z-1 w-2.5 h-0.5 bg-white pointer-events-none">
                <span className="absolute top-1/2 left-1/2 w-0.5 h-2.5 bg-white -translate-x-1/2 -translate-y-1/2" />
              </span>
            </Button>
          </div>
          <div className="w-10.5 h-10.5 p-1 bg-dark-900 rounded-xl">
            <Button
              className="group relative size-8.5 rounded-xl"
              icon
              onClick={() => handleRemove(item.id)}
              variant="soft"
              color="gray"
            >
              <span className="absolute size-full top-0 left-0 bg-gradient-accent rounded-xl opacity-0 transition-all ease-in-out duration-200 group-hover:opacity-100" />
              <BucketIcon className="relative z-1 size-4" />
            </Button>
          </div>
        </div>
        <div className="flex flex-col justify-center p-2.5 items-center border border-[#1F1F1F] rounded-2xl">
          <p className="text-15 font-bold">{formatPrice(item.count * item.price)}₽</p>
          <p className="text-15 font-medium opacity-60">
            {t("business.goodsAndWarehouse.goodsPrice")}
          </p>
        </div>
      </div>
    </div>
  );
}
